import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { forkJoin, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { BillingConfiguration } from "src/app/backend/models/billing-configuration";
import { UserNotify } from "src/app/support/notify-decorator";
import { sntTranslate } from "src/app/support/translate";
import { environment } from "src/environments/environment";
import { Configuration } from "../models/configuration";
import { AppointmentConfiguration } from "src/app/backend/models/appointment-configuration";
import { HealthcareConfiguration } from "src/app/backend/models/healthcare-configuration";
import { CacheIfFail } from "src/app/support/decorators/cache-if-fails-decorator";
import {
  WarehouseConfiguration,
  WarehouseConfigurationUpdate
} from "src/app/backend/models/configuration/warehouse-configuration";
import { PaginatedResult } from "src/app/support/paginated-result";
import { first } from "lodash";
import { OrganizationConfiguration } from "src/app/backend/models/organization-configuration";

@Injectable()
export class ConfigurationBackend {
  constructor(private readonly http: HttpClient) {}

  load(): Observable<Configuration> {
    return this.http.get<Configuration>(
      `${environment.snoots.api.diagnostic}/configurations`,
      {
        headers: {
          "snt-product": "rocket"
        }
      }
    );
  }

  loadPms(): Observable<Configuration> {
    return this.http
      .get<Configuration>(`${environment.snoots.api.healthcare}/configurations`)
      .pipe(
        map((configuration) => {
          return {
            ...configuration,
            header: explodeHeader(<any>configuration.header)
          };
        })
      );
  }

  @CacheIfFail("warehouse-configuration")
  loadWarehouse(): Observable<WarehouseConfiguration | undefined> {
    return this.http
      .get<PaginatedResult<WarehouseConfiguration>>(
        `${environment.snoots.api.warehouse}/configurations`
      )
      .pipe(map((response) => first(response.docs)));
  }

  @CacheIfFail("billing-configuration")
  loadBilling(): Observable<BillingConfiguration> {
    return this.http.get<BillingConfiguration>(
      `${environment.snoots.api.billing}/configurations`
    );
  }

  @CacheIfFail("organization-configuration")
  loadOrganization(): Observable<OrganizationConfiguration> {
    return this.http
      .get<OrganizationConfiguration>(
        `${environment.snoots.api.organization}/configurations`
      )
      .pipe(
        map((configuration: OrganizationConfiguration) => {
          return <OrganizationConfiguration>{
            ...configuration,
            signature: {
              type: "otp"
            }
          };
        }),
        catchError(() => {
          return of(<OrganizationConfiguration>{
            signature: {
              type: "otp"
            }
          });
        })
      );
  }

  loadCalendar(): Observable<AppointmentConfiguration> {
    return this.http.get<AppointmentConfiguration>(
      `${environment.snoots.api.calendar}/configurations`
    );
  }

  loadHealthcare(): Observable<HealthcareConfiguration> {
    return this.http.get<HealthcareConfiguration>(
      `${environment.snoots.api.healthcare}/configurations`
    );
  }

  loadHealthcarePublic(
    tenantId: string
  ): Observable<Pick<HealthcareConfiguration, "survey">> {
    return this.http.get<HealthcareConfiguration>(
      `${environment.snoots.api.healthcare}/public/configurations/${tenantId}`
    );
  }

  @UserNotify({
    successMessage: sntTranslate(marker("Configurazione aggiornata")),
    errorMessage: sntTranslate(
      marker("Impossibile aggiornare la configurazione")
    )
  })
  update(id: string, payload: Partial<Configuration>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.diagnostic}/configurations/${id}`,
      payload
    );
  }

  @UserNotify({
    successMessage: sntTranslate(marker("Configurazione aggiornata")),
    errorMessage: sntTranslate(
      marker("Impossibile aggiornare la configurazione")
    )
  })
  updatePms(id: string, payload: Partial<Configuration>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/configurations/${id}`,
      {
        ...payload,
        header: payload.header ? implodeHeader(payload.header) : payload.header
      }
    );
  }

  updateWarehouse(id: string, payload: WarehouseConfigurationUpdate) {
    return this.http.patch<void>(
      `${environment.snoots.api.warehouse}/configurations/${id}`,
      payload
    );
  }

  @UserNotify({
    successMessage: sntTranslate(marker("Configurazione aggiornata")),
    errorMessage: sntTranslate(
      marker("Impossibile aggiornare la configurazione")
    )
  })
  updateBilling(
    id: string,
    payload: Partial<BillingConfiguration>
  ): Observable<void> {
    return this.updateBillingSilently(id, payload);
  }

  updateBillingSilently(
    id: string,
    payload: Partial<BillingConfiguration>
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/configurations/${id}`,
      {
        ...payload
      }
    );
  }

  @UserNotify({
    successMessage: sntTranslate(marker("Configurazione aggiornata")),
    errorMessage: sntTranslate(
      marker("Impossibile aggiornare la configurazione")
    )
  })
  updateCalendar(
    id: string,
    payload: Partial<AppointmentConfiguration>
  ): Observable<void> {
    return this.updateCalendarSilently(id, payload);
  }

  updateCalendarSilently(
    id: string,
    payload: Partial<AppointmentConfiguration>
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.calendar}/configurations/${id}`,
      {
        ...payload
      }
    );
  }

  updateHealthcareSilently(
    id: string,
    payload: Partial<HealthcareConfiguration>
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/configurations/${id}`,
      {
        ...payload
      }
    );
  }

  updateOrganizationSilently(
    id: string,
    payload: Partial<OrganizationConfiguration>
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.organization}/configurations/${id}`,
      {
        ...payload
      }
    );
  }

  @UserNotify({
    successMessage: sntTranslate(marker("Configurazione aggiornata")),
    errorMessage: sntTranslate(
      marker("Impossibile aggiornare la configurazione")
    )
  })
  updateBillingCalendarHealthcareConfiguration(
    {
      billingId,
      billingConfiguration
    }: {
      billingId: string;
      billingConfiguration: Partial<BillingConfiguration>;
    },
    {
      calendarId,
      calendarConfiguration
    }: {
      calendarId: string;
      calendarConfiguration: Partial<AppointmentConfiguration>;
    },
    {
      healthcareId,
      healthcareConfiguration
    }: {
      healthcareId: string;
      healthcareConfiguration: Partial<HealthcareConfiguration>;
    },
    {
      organizationId,
      organizationConfiguration
    }: {
      organizationId: string;
      organizationConfiguration: Partial<OrganizationConfiguration>;
    }
  ): Observable<unknown> {
    return forkJoin([
      this.updateBillingSilently(billingId, billingConfiguration),
      this.updateCalendarSilently(calendarId, calendarConfiguration),
      this.updateHealthcareSilently(healthcareId, healthcareConfiguration),
      this.updateOrganizationSilently(organizationId, organizationConfiguration)
    ]);
  }
}

function explodeHeader(data: string | undefined): {
  html: string | undefined;
  logo: string | undefined;
  secondaryImage: string | undefined;
} {
  try {
    if (!data) {
      return {
        logo: "",
        secondaryImage: "",
        html: ""
      };
    }

    return JSON.parse(data);
  } catch (ex) {
    return {
      logo: "",
      secondaryImage: "",
      html: ""
    };
  }
}

function implodeHeader(headerData: {
  html: string | undefined;
  logo: string | undefined;
  secondaryImage: string | undefined;
}): string {
  return JSON.stringify(headerData);
}
